import { Link } from "react-router-dom";
import type { Article } from "@/types/news";

interface HeroSectionProps {
  featured: Article;
  latest: Article[];
}

export default function HeroSection({ featured, latest }: HeroSectionProps) {
  return (
    <section className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10" aria-labelledby="today-headline">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <article className="relative col-span-1 overflow-hidden rounded-lg lg:col-span-2">
          <div className="relative h-[380px] w-full md:h-[460px]">
            <img
              src={featured.cover}
              alt={`${featured.title} 主图`}
              title={`${featured.title} - 爆料官网今日发布`}
              className="absolute inset-0 h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/92 via-foreground-950/45 to-foreground-950/10"></div>
          </div>
          <div className="absolute inset-x-0 bottom-0 p-5 md:p-8">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="flex items-center gap-1 rounded bg-primary-500 px-2 py-1 font-label text-[11px] text-background-50 dark:text-foreground-950">
                <i className="ri-flashlight-fill" aria-hidden="true"></i>
                今日头条
              </span>
              <span className="rounded border border-background-50/30 px-2 py-1 font-label text-[11px] text-background-100">
                {featured.channelName}
              </span>
            </div>
            <h2 id="today-headline" className="max-w-2xl font-heading text-2xl font-bold leading-snug text-background-50 md:text-4xl">
              <Link to={`/article/${featured.id}`} className="transition-opacity hover:opacity-90">
                {featured.title}
              </Link>
            </h2>
            <p className="mt-3 max-w-2xl font-body text-sm leading-6 text-background-200 md:text-base">
              {featured.summary}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 font-label text-xs text-background-300">
              <span>{featured.author}</span>
              <span className="hidden h-3 w-px bg-background-50/30 sm:block"></span>
              <span>{featured.publishedAt}</span>
              <span className="hidden h-3 w-px bg-background-50/30 sm:block"></span>
              <span>阅读 {(featured.views / 10000).toFixed(1)} 万</span>
            </div>
          </div>
        </article>

        <aside className="flex flex-col rounded-lg border border-background-200 bg-background-100 p-5" aria-label="最新动态">
          <div className="flex items-center justify-between border-b border-background-300 pb-3">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="flex h-5 w-5 items-center justify-center text-primary-500">
                <i className="ri-pulse-line text-lg" aria-hidden="true"></i>
              </span>
              最新动态
            </h2>
            <span className="font-label text-xs text-secondary-500">实时更新</span>
          </div>
          <ul className="mt-2 flex flex-1 flex-col">
            {latest.map((item, index) => (
              <li key={item.id} className="border-b border-background-200 last:border-b-0">
                <Link to={`/article/${item.id}`} className="group flex gap-3 py-3">
                  <span
                    className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded font-label text-xs font-semibold ${
                      index === 0
                        ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                        : "bg-background-300 text-secondary-800"
                    }`}
                  >
                    {index + 1}
                  </span>
                  <span className="min-w-0">
                    <span className="line-clamp-2 font-body text-sm leading-6 text-foreground-800 transition-colors group-hover:text-primary-600">
                      {item.title}
                    </span>
                    <span className="mt-1 block font-label text-xs text-secondary-500">
                      {item.publishedAt.slice(5, 16)}
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <Link
            to="/channel/all"
            className="mt-4 flex items-center justify-center gap-1 rounded-md border border-background-300 py-2.5 font-label text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
          >
            浏览全部动态
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </aside>
      </div>
    </section>
  );
}